package pl.decks.personal.components.layouts.site

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onAnimationEnd
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.icons.fa.FaBars
import com.varabyte.kobweb.silk.components.icons.fa.FaMoon
import com.varabyte.kobweb.silk.components.icons.fa.FaSun
import com.varabyte.kobweb.silk.components.icons.fa.FaX
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.style.vars.animation.TransitionDurationVars
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.AnimationDirection
import org.jetbrains.compose.web.css.AnimationFillMode
import org.jetbrains.compose.web.css.AnimationTimingFunction
import pl.decks.personal.components.animations.AppearAnimation
import pl.decks.personal.components.animations.SlideFromTopAnimation
import pl.decks.personal.components.layouts.site.SiteLayoutStylesheet.Header
import pl.decks.personal.components.layouts.site.SiteLayoutStylesheet.Menu
import pl.decks.personal.components.widgets.button.ButtonStylesheet
import pl.decks.personal.components.widgets.button.IconButton
import pl.decks.personal.extensions.thenIf
import pl.decks.posthog.client.PostHogClient

private typealias NavigationTarget = Pair<String, String>

private val NavigationTarget.text: String get() = this.first
private val NavigationTarget.target: String get() = this.second
private val NAVIGATION_TARGETS = listOf<NavigationTarget>(
    "Home" to "/",
    "IEVP" to "/ievp",
    "Blog" to "/blog"
)

@Composable
internal fun SiteLayoutScope.Header() = Column(
    modifier = Header.Style.toModifier(
        if (menuState != CollapsableMenuState.CLOSED) Header.ExpandedVariant else Header.CollapsedVariant
    )
) {
    val menuState = this@Header.menuState
    val onNavigate = { if (this@Header.menuState != CollapsableMenuState.CLOSED) this@Header.closeMenu() }
    HeaderBar(
        menuState,
        onMenuIconClicked = { this@Header.switchMenuState() },
        onNavigate = onNavigate,
    )
    if (menuState != CollapsableMenuState.CLOSED) {
        CollapsableNavigation(
            menuState,
            onFinishClosing = { if (menuState == CollapsableMenuState.CLOSING) this@Header.finishClosingMenu() },
            onNavigate = onNavigate,
        )
    }
}


@Composable
private fun HeaderBar(
    menuState: CollapsableMenuState,
    onMenuIconClicked: () -> Unit,
    onNavigate: () -> Unit,
) = Row(
    modifier = Header.Bar.toModifier(),
    verticalAlignment = Alignment.CenterVertically,
) {
    HomeLogoLink(onNavigate)
    Spacer()
    InlineNavigation(onNavigate = onNavigate)
    ColorModeButton()
    HamburgerMenuButton(menuState, onMenuIconClicked)
}


@Composable
private fun HomeLogoLink(onNavigate: () -> Unit) = Link(
    "/",
    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
    modifier = Modifier.onClick {
        PostHogClient.capture(SiteNavigationJourney.LogoBannerClicked)
        onNavigate()
    }
) { SiteLogo() }

@Composable
private fun InlineNavigation(onNavigate: () -> Unit) =
    Row(Menu.Inline.toModifier().displayIfAtLeast(Breakpoint.MD)) {
        NAVIGATION_TARGETS.forEach { target -> NavLink(target, onNavigate = onNavigate) }
    }

@Composable
private fun ColorModeButton() {
    var colorMode by ColorMode.currentState
    IconButton(
        onClick = {
            colorMode = colorMode.opposite
            PostHogClient.capture(SiteNavigationJourney.ColorModeSelected(colorMode))
        },
        variant = ButtonStylesheet.GhostVariant,
    ) {
        when (colorMode) {
            ColorMode.LIGHT -> FaMoon()
            ColorMode.DARK -> FaSun()
        }
    }
}

@Composable
private fun HamburgerMenuButton(
    menuState: CollapsableMenuState,
    onClick: () -> Unit,
) = IconButton(
    onClick = onClick,
    modifier = Modifier.displayUntil(Breakpoint.MD),
    variant = ButtonStylesheet.GhostVariant,
) {
    when (menuState) {
        CollapsableMenuState.CLOSED -> FaBars()
        else -> FaX()
    }
}

@Composable
private fun CollapsableNavigation(
    menuState: CollapsableMenuState,
    onFinishClosing: () -> Unit,
    onNavigate: () -> Unit,
) = key(menuState) {
    val animationTimingFunction =
        if (menuState == CollapsableMenuState.OPEN) AnimationTimingFunction.EaseOut else AnimationTimingFunction.EaseIn
    val animationDirection =
        if (menuState == CollapsableMenuState.OPEN) AnimationDirection.Normal else AnimationDirection.Reverse
    Column(
        modifier = Menu.Collapsable.toModifier()
            .displayUntil(Breakpoint.MD)
            .animation(
                SlideFromTopAnimation.Frames.toAnimation(
                    duration = TransitionDurationVars.Normal.value(),
                    timingFunction = animationTimingFunction,
                    direction = animationDirection,
                    fillMode = AnimationFillMode.Forwards,
                ),
                AppearAnimation.Frames.toAnimation(
                    duration = TransitionDurationVars.Normal.value(),
                    timingFunction = animationTimingFunction,
                    direction = animationDirection,
                    fillMode = AnimationFillMode.Forwards,
                )
            )
            .onAnimationEnd { onFinishClosing() }
    ) {
        NAVIGATION_TARGETS.forEachIndexed { index, target ->
            NavLink(target, modifier = Modifier.fillMaxWidth(), onNavigate = onNavigate)
            if (index != NAVIGATION_TARGETS.lastIndex) HorizontalDivider()
        }
    }
}

@Composable
private fun NavLink(navTarget: NavigationTarget, modifier: Modifier = Modifier, onNavigate: () -> Unit) = Link(
    navTarget.target,
    navTarget.text,
    modifier = Modifier
        .onClick {
            PostHogClient.capture(SiteNavigationJourney.HeaderNavigated(navTarget.target))
            onNavigate()
        }
        .then(modifier),
    variant = UndecoratedLinkVariant
        .then(UncoloredLinkVariant)
        .then(Menu.EntryLinkVariant)
        .thenIf(isCurrentASubpathOf(navTarget.target), Menu.EntryLinkSelectedVariant),
)

@Composable
private fun isCurrentASubpathOf(targetPath: String): Boolean {
    val ctx = rememberPageContext()
    val current = ctx.route.path.removeSuffix("/")
    val target = targetPath.removeSuffix("/")
    return if (target == "") current == "" else current.startsWith(target)
}
